import * as React from "react";
import type { MemoryGraph, MemoryGraphNode, MemoryEntry } from "@zworks/console-contracts";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, RotateCcw, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { buildLightRagGraph, layoutGraphNodes, type PositionedGraphNode } from "./graph-layout";
import { MemoryNodeInspector } from "./memory-node-inspector";
import { cn } from "@/lib/utils";

interface MemoryGraphCanvasProps {
  entries: MemoryEntry[];
  projectName: string;
}

export function MemoryGraphCanvas({ entries, projectName }: MemoryGraphCanvasProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Pan and Zoom transform state
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const [scale, setScale] = React.useState(1);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

  // Node selection & search filter
  const [selectedNode, setSelectedNode] = React.useState<MemoryGraphNode | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Canvas bounds
  const [dimensions, setDimensions] = React.useState({ width: 900, height: 560 });

  React.useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth || 900,
          height: Math.max(540, containerRef.current.clientHeight || 560),
        });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const graph: MemoryGraph = React.useMemo(
    () => buildLightRagGraph(entries, projectName),
    [entries, projectName],
  );

  const positionedNodes: PositionedGraphNode[] = React.useMemo(
    () => layoutGraphNodes(graph, dimensions.width, dimensions.height),
    [graph, dimensions],
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    if (
      (e.target as HTMLElement).tagName.toLowerCase() === "svg" ||
      (e.target as HTMLElement).id === "graph-bg"
    ) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const zoomFactor = e.deltaY < 0 ? 1.08 : 0.92;
    setScale((prev) => Math.min(2.5, Math.max(0.4, prev * zoomFactor)));
  };

  const resetZoom = () => {
    setPan({ x: 0, y: 0 });
    setScale(1);
  };

  const zoomIn = () => setScale((prev) => Math.min(2.5, prev * 1.2));
  const zoomOut = () => setScale((prev) => Math.max(0.4, prev * 0.8));

  // Node mapping for fast lookup
  const nodeCoordsMap = React.useMemo(() => {
    const map = new Map<string, PositionedGraphNode>();
    for (const node of positionedNodes) {
      map.set(node.id, node);
    }
    return map;
  }, [positionedNodes]);

  return (
    <div className="flex flex-col gap-4">
      {/* Top Controls Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search
            className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search concepts, entities, relations..."
            className="h-8 pl-8 text-xs"
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-lg border border-border/70 bg-card p-1 shadow-2xs">
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded"
              onClick={zoomIn}
              aria-label="Zoom in"
              title="Zoom in"
            >
              <ZoomIn className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded"
              onClick={zoomOut}
              aria-label="Zoom out"
              title="Zoom out"
            >
              <ZoomOut className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded"
              onClick={resetZoom}
              aria-label="Reset zoom"
              title="Reset view"
            >
              <RotateCcw className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Canvas & Inspector Layout */}
      <div
        className={cn(
          "grid gap-4 transition-all duration-200",
          selectedNode ? "lg:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.85fr)]" : "grid-cols-1",
        )}
      >
        {/* Interactive Canvas Viewport */}
        <div
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onWheel={handleWheel}
          className="relative min-h-[560px] h-[600px] w-full overflow-hidden rounded-lg border border-border/70 bg-muted/20 select-none shadow-xs cursor-grab active:cursor-grabbing"
        >
          {/* SVG Coordinate Layer */}
          <svg
            id="graph-bg"
            className="absolute inset-0 size-full"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
              transformOrigin: "center center",
            }}
          >
            <defs>
              {/* Dot mesh pattern */}
              <pattern id="lightrag-mesh" width="28" height="28" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1.2" className="fill-border/60" />
              </pattern>
              {/* Marker Arrowhead */}
              <marker
                id="graph-arrow"
                viewBox="0 0 10 10"
                refX="22"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 1 L 9 5 L 0 9 z" className="fill-muted-foreground/60" />
              </marker>
            </defs>

            {/* Mesh Background */}
            <rect width="10000" height="10000" x="-5000" y="-5000" fill="url(#lightrag-mesh)" />

            {/* Connecting Edges */}
            <g className="edges">
              {graph.edges.map((edge) => {
                const src = nodeCoordsMap.get(edge.source);
                const tgt = nodeCoordsMap.get(edge.target);
                if (!src || !tgt) return null;

                // Compute curved Bezier line
                const midX = (src.x + tgt.x) / 2;
                const midY = (src.y + tgt.y) / 2;
                const path = `M ${src.x} ${src.y} Q ${midX} ${midY - 14} ${tgt.x} ${tgt.y}`;

                return (
                  <g key={edge.id} className="transition-opacity group">
                    <path
                      d={path}
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                      markerEnd="url(#graph-arrow)"
                      className="text-border/80 group-hover:text-chart transition-colors"
                    />
                    {/* Relation label badge */}
                    <rect
                      x={midX - 24}
                      y={midY - 21}
                      width="48"
                      height="14"
                      rx="4"
                      className="fill-card stroke-border/70"
                    />
                    <text
                      x={midX}
                      y={midY - 10}
                      textAnchor="middle"
                      className="font-mono text-xs fill-muted-foreground select-none"
                    >
                      {edge.relation}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Interactive Graph Nodes */}
            <g className="nodes">
              {positionedNodes.map((node) => {
                const isSelected = selectedNode?.id === node.id;
                const isConcept = node.type === "concept";

                // Filter opacity if search active
                const matchesSearch =
                  !searchQuery ||
                  node.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  node.description.toLowerCase().includes(searchQuery.toLowerCase());

                return (
                  <g
                    key={node.id}
                    transform={`translate(${node.x}, ${node.y})`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedNode(node);
                    }}
                    className={cn(
                      "cursor-pointer transition-transform duration-150",
                      isSelected ? "scale-110" : "hover:scale-105",
                      !matchesSearch && "opacity-25",
                    )}
                  >
                    {/* Outer Glow Halo for selected or concept */}
                    <circle
                      r={node.radius + (isSelected ? 7 : isConcept ? 4 : 2)}
                      className={cn(
                        "transition-all",
                        isSelected
                          ? "fill-chart/20 stroke-chart stroke-2"
                          : isConcept
                            ? "fill-lime/10 stroke-lime/40"
                            : "fill-transparent stroke-transparent",
                      )}
                    />

                    {/* Main Node Body Circle */}
                    <circle
                      r={node.radius}
                      className={cn(
                        "transition-colors shadow-sm",
                        isConcept
                          ? "fill-card stroke-lime stroke-2 text-lime"
                          : "fill-card stroke-border/80 stroke-1 text-foreground",
                      )}
                    />

                    {/* Icon or Type Symbol */}
                    <text
                      x="0"
                      y="-4"
                      textAnchor="middle"
                      className={cn(
                        "font-mono text-xs select-none font-bold",
                        isConcept ? "fill-chart" : "fill-foreground",
                      )}
                    >
                      {isConcept ? "●" : "■"}
                    </text>

                    {/* Node Text Label */}
                    <text
                      x="0"
                      y="12"
                      textAnchor="middle"
                      className="font-sans text-xs font-semibold fill-foreground select-none pointer-events-none"
                    >
                      {node.name.length > 18 ? node.name.slice(0, 16) + ".." : node.name}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>

          {/* Floating Canvas Legend */}
          <div className="absolute bottom-3 left-3 flex items-center gap-3 rounded-lg border border-border/70 bg-card/90 px-3 py-1.5 text-xs backdrop-blur-sm shadow-xs">
            <span className="flex items-center gap-1.5 font-medium">
              <span className="size-2.5 rounded-full bg-lime border border-lime/40" /> Concept
              (Theme)
            </span>
            <span className="flex items-center gap-1.5 font-medium">
              <span className="size-2.5 rounded-sm bg-muted-foreground/60" /> Entity (Spec)
            </span>
          </div>
        </div>

        {/* Right Drawer Inspector */}
        {selectedNode && (
          <div className="h-[600px] overflow-hidden rounded-lg shadow-2xs">
            <MemoryNodeInspector
              node={selectedNode}
              entries={entries}
              onClose={() => setSelectedNode(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
